import {autobind} from 'core-decorators';

import dialog from '../dialog';
import {addAuthorizationHeaderIfPresent, appDebug, getIdForUser, noop, window} from '../util';
import emitter from '../util/emitter';
import {BaseService} from './base';
import type {ResponseAndBody} from './apiService';

const moduleDebug = appDebug.extend('authService');


function getGoogleUserFromWindow(): null|any {
    const functionDebug = moduleDebug.extend('getGoogleUserFromWindow');
    functionDebug('window.gapi', window.gapi);
    if (window.gapi) {
        const GoogleAuth = window.gapi.auth2.getAuthInstance();
        functionDebug('GoogleAuth', GoogleAuth);
        if (GoogleAuth) {
            const GoogleUser = GoogleAuth.currentUser.get();
            functionDebug('GoogleUser', {
                GoogleUser,
                getGrantedScopes: GoogleUser.getGrantedScopes(),
                getAuthResponse: GoogleUser.getAuthResponse(),
            });
            return GoogleUser;
        }
    }
    functionDebug('returning null');
    return null;
}

type AuthData = {
    user: api.User,
    GoogleUser?: any,
    googleUserEmail?: null|string,
}

export class AuthService extends BaseService {
    checkGoogleAccessTokenTimeoutRef?: ReturnType<
        typeof window.setTimeout>|number|null;
    timedLogoutTimeoutRef?: ReturnType<typeof window.setTimeout>|number|null;
    initResolver: ReturnType<typeof Promise.withResolvers<void>>;
    storageKey: string;
    pending: boolean;
    tmpGoogleUser: any;
    authData: null|AuthData;

    constructor(context: jsx.AppGlobals) {
        super(context, {});

        this.initResolver = Promise.withResolvers();
        this.storageKey = 'authData';

        this.pending = true;
        this.authData = null;

        const functionDebug = moduleDebug.extend('constructor');
        const storedAuthData = this.context.storageService.getItem(
            this.storageKey);
        window.addEventListener('storage', (event) => {
            const handlerDebug = moduleDebug.extend('storageChanged');
            handlerDebug('storage updated in another tab/window', event);
            if (event.key === this.storageKey) {
                handlerDebug('authData updated in another tab/window');
                // We need to make sure in the first tab we don't get other
                // tabs sending us back the data we just sent them.
                if (event.oldValue !== event.newValue) {
                    handlerDebug('event.newValue', event.newValue);
                    const data = event.newValue ?
                        JSON.parse(event.newValue) : null;
                    if (data) {
                        const GoogleUser = getGoogleUserFromWindow();
                        if (GoogleUser) {
                            data.GoogleUser = GoogleUser;
                        }
                    }
                    this.setAuthData(data);
                }
            }
        });
        window.authExport = (log: boolean) => {
            const val = this.context.storageService.getItem(this.storageKey);
            if (log) {
                this.context.request.console.log(
                    `authImport(${JSON.stringify(JSON.stringify(val))})`);
            } else {
                if (val) {
                    return JSON.parse(val);
                }
            }
        };
        window.authImport = (data: any) => {
            this.context.storageService.setItem(
                this.storageKey, JSON.parse(data));
        };
        functionDebug('this.authData!!!!!!!!!!!', this.authData);
        if (storedAuthData) {
            // Skip `setAuthData()` to avoid a bunch of callbacks, etc. We
            // should treat this stored data as if the user was already
            // logged in.
            this.authData = JSON.parse(storedAuthData);

            const handleGoogleAuthLoadChange = () => {
                const GoogleUser = getGoogleUserFromWindow();
                if (GoogleUser) {
                    this.updateAuthData({GoogleUser});
                }
                // Now that GoogleAuth might be loaded, we should check if
                // the token is expired.
                this.checkGoogleAccessTokenExpiration()
                    .finally(() => this.initResolver.resolve());
                functionDebug('setting pending to false');
                this.pending = false;
                emitter.emit('authDataSet');
            };
            process.env.BROWSER && emitter.on('googleAuthLoadChange',
                handleGoogleAuthLoadChange);
            functionDebug('googleAuthService.getClientConfig()',
                this.context.googleAuthService.getClientConfig());
            this.context.googleAuthService.load();
        } else {
            functionDebug('no storedAuthData, setting pending to false');
            this.pending = false;
            this.initResolver.resolve();
        }
    }
    destruct() {
        this.checkGoogleAccessTokenTimeoutRef = null;
        this.timedLogoutTimeoutRef = null;
    }
    logout({msgToUser}: {msgToUser?: string}): Promise<void> {
        this.setAuthData(null);
        if (msgToUser) {
            dialog.open('popup-dialog', {
                closeDelay: 2500,
                children: (
                    <div>
                        <h1>{msgToUser}</h1>
                    </div>
                ),
            });
        }
        window.gaEvent({
            category: 'Account',
            action: 'logout',
        });
        return Promise.resolve();
    }
    deleteAccount(): Promise<void> {
        this.context.apiService.registerEvent('account_deletion_requested');
        let promise;
        const data = {
            profile_id: this.getId(),
        };
        const headers = new Headers();
        const googleAccessToken = this.getGoogleAccessToken();
        addAuthorizationHeaderIfPresent(headers, googleAccessToken);
        promise = this.context.apiService.postV2(
            '/delete_profile', data, null, headers,
        );
        promise = promise.then((
            {body, response}: ResponseAndBody<api.StrBody>) => {
            if (response?.ok) {
                this.context.apiService.registerEvent(
                    'account_deletion_completed', {tag: 'success'});
                this.setAuthData(null);
                return Promise.resolve();
            }
            this.context.apiService.registerEvent(
                'account_deletion_completed', {tag: 'failure'});
            return Promise.reject();
        });
        window.gaEvent({
            category: 'Account',
            action: 'delete',
        });
        return promise;
    }
    storeAuthData() {
        const authData = {...this.authData};
        delete authData.GoogleUser;
        this.context.storageService.setItem(
            this.storageKey, JSON.stringify(authData));
    }
    setAuthData(data: AuthData|null, signup?: boolean) {
        moduleDebug('setAuthData', data, signup);
        this.authData = data;
        // We need a new apirefid, because the previous one is associated with
        // either an anon_id or a profile ID. `newApirefid` needs to be called
        // immediately after setting this.authData.
        this.context.apiService.newApirefid();
        if (this.authData) {
            if (this.authData.GoogleUser) {
                this.tmpGoogleUser = null;
                const basicProfile =
                    this.authData.GoogleUser.getBasicProfile();
                this.authData.googleUserEmail = basicProfile?.getEmail();
            }
            this.storeAuthData();

            // We don't unset userId on logout because we can generally
            // assume it's still the same person.
            window.ga('set', 'userId', this.getId());
            window.gtag({userId: this.getId()});
            if (signup) {
                window.gaEvent({
                    category: 'Account',
                    action: 'signup',
                });
            } else {
                window.gaEvent({
                    category: 'Account',
                    action: 'login',
                });
            }

            this.checkGoogleAccessTokenExpiration().finally(() => {
                this.context.apiService.fetchFavorites();
                this.addTimedLogout();
            });
        } else {
            this.context.uploadService.clearAll();
            this.context.cacheService.clearFavorites();
            this.context.storageService.removeItem(this.storageKey);
        }

        this.context.apiService.getConfig();
        emitter.emit('authDataSet');
    }
    updateAuthData(data: Partial<AuthData>) {
        moduleDebug('updateAuthData', this.authData, data);
        if (!this.authData) {
            return;
        }
        if (this.authData.GoogleUser) {
            this.tmpGoogleUser = null;
            const basicProfile =
                this.authData.GoogleUser.getBasicProfile();
            this.authData.googleUserEmail = basicProfile?.getEmail();
        }
        Object.assign(this.authData, data);
        this.checkGoogleAccessTokenExpiration().finally(() => {
            this.context.apiService.fetchFavorites();
        });
        this.storeAuthData();
        emitter.emit('authDataSet');
    }
    @autobind
    handleInit() {
        if (this.isLoggedIn()) {
            this.context.apiService.fetchFavorites();
        }
    }
    initWhenReady() {
        this.initResolver.promise.then(this.handleInit);
    }
    addTimedLogout() {
        clearTimeout(this.timedLogoutTimeoutRef);
        const expires = this.getExpires();
        if (expires) {
            this.timedLogoutTimeoutRef = window.setTimeout(
                () => {
                    moduleDebug('timed logout!!!!!!!!!!!!!');
                    this.logout({
                        msgToUser: 'Your session has expired, please log in again.',
                    });
                }, 1000 * Math.floor(expires - (Date.now() / 1000)),
            );
        }
    }
    isLoggedIn() {
        return Boolean(this.authData);
    }
    getExpires() {
        // FIXME: this used to use legacy auth expiration data, it should be
        // updated for the Google auth token expiration date.
        return null;
    }
    getLegacyToken() {
        return null;
    }
    getGoogleUser() {
        if (this.tmpGoogleUser) {
            return this.tmpGoogleUser;
        }
        return this.authData ? this.authData.GoogleUser : null;
    }
    /**
     * Returns the email for the GoogleUser at the time the authData was
     * stored, since it can change to an incorrect value.
     */
    getGoogleUserEmail(): string|null {
        return this.authData.googleUserEmail || null;
    }
    getGoogleAuthResponse() {
        const GoogleUser = this.getGoogleUser();
        if (GoogleUser) {
            return GoogleUser.getAuthResponse(true);
        }
        return null;
    }
    getGoogleAccessToken() {
        const googleAuthResponse = this.getGoogleAuthResponse();
        return googleAuthResponse ? googleAuthResponse.access_token : null;
    }
    getGaiaToken() {
        const googleAuthResponse = this.getGoogleAuthResponse();
        return googleAuthResponse ? googleAuthResponse.id_token : null;
    }
    hasOAuthScope(scope: string) {
        const GoogleUser = this.getGoogleUser();
        if (GoogleUser?.hasGrantedScopes(scope)) {
            moduleDebug('hasOAuthScope', scope, 'true');
            return true;
        }
        moduleDebug('hasOAuthScope', scope, 'false');
        return false;
    }
    hasTenorOAuthScope() {
        return this.hasOAuthScope(this.context.googleAuthService.TENOR_SCOPE);
    }
    getId() {
        const user = this.authData?.user;
        return getIdForUser(this.authData?.user);
    }
    getUser() {
        return this.authData ? this.authData.user : {};
    }
    getUsername() {
        return this.authData?.user ? this.authData.user.username : null;
    }
    /**
     * @returns {boolean|undefined} true/false if the profile is owned by
     * current user, undefined if we don't know
     * because auth status hasn't completed.
     */
    ownsProfile(profile: string): boolean|undefined {
        if (this.pending) {
            return undefined;
        }
        const username = this.getUsername();
        return username ?
            username.toLowerCase() === profile.toLowerCase() : false;
    }
    userHasFlag(flag: string): boolean {
        return this.authData?.user?.flags.indexOf(flag) > -1;
    }
    getUserFlags(): string[] {
        return this.authData ? this.authData.user.flags : [];
    }
    showLoginDialog(dialogData = {}) {
        dialog.open('auth-dialog', dialogData);
    }
    showScopeRefreshDialog({loggedinCallback, permissionText}: {
        loggedinCallback: (...args: any[]) => any,
        permissionText?: string,
    }) {
        dialog.open('auth-dialog', {
            mode: 'scope-refresh',
            loggedinCallback,
            permissionText,
        });
    }
    checkGoogleAccessTokenExpiration() {
        const functionDebug = moduleDebug.extend(
            'checkGoogleAccessTokenExpiration');
        const googleAuthResponse = this.getGoogleAuthResponse();
        functionDebug('checkGoogleAccessTokenExpiration', {
            googleAuthResponse,
            GoogleUser: this.getGoogleUser(),
        });
        functionDebug('hasTenorOAuthScope', this.hasTenorOAuthScope());
        if (!googleAuthResponse) {
            return Promise.resolve();
        }
        // Refresh if about to expire in 2 minutes or sooner.
        const expired = (
            googleAuthResponse.expires_at - (2 * 60 * 1000)) < Date.now();
        if (expired) {
            functionDebug('Google token expired.');
            const GoogleUser = this.getGoogleUser();
            if (!GoogleUser) {
                functionDebug('No GoogleUser, logout.');
                this.logout({msgToUser: 'Google user information is missing and you have been logged out, please log in again.'});
                return Promise.reject();
            } else {
                return new Promise((resolve, reject) => {
                    // This isn't a Promise, but a Promise-like object
                    // that's missing `finally()`.
                    GoogleUser.reloadAuthResponse().then(resolve, reject);
                }).then((googleAuthResponse) => {
                    functionDebug('Google token refresh succeeded.',
                        googleAuthResponse);
                    this.updateAuthData({GoogleUser});
                    this.checkGoogleAccessTokenExpiration();
                }, () => {
                    functionDebug('Google token refresh failed.');
                    this.logout({msgToUser: 'We could not refresh your session and you have been logged out, please log in again.'});
                });
            }
        } else {
            // Check 30 seconds from now.
            functionDebug('Google token not expired, checking again in 30 seconds.');
            // Need to clean up the last one before rescheduling.
            if (this.checkGoogleAccessTokenTimeoutRef) {
                window.clearTimeout(this.checkGoogleAccessTokenTimeoutRef);
            }
            this.checkGoogleAccessTokenTimeoutRef = window.setTimeout(() => {
                this.checkGoogleAccessTokenExpiration();
            }, 30 * 1000);
        }
        return Promise.resolve();
    }
}
